import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input, Select } from '../components/ui/Input';
import { PlayIcon, TrendingUpIcon, UsersIcon, TargetIcon, RocketIcon, BarChartIcon, CheckCircle2Icon, StarIcon, CalendarIcon, ArrowRightIcon, VolumeX, XIcon } from 'lucide-react';
import { initPixel, trackCtaClick, trackVideoPlay, trackLead, trackFormStart } from '../utils/metaPixel';
import { initClarity } from '../utils/clarity';
import { initGtag } from '../utils/gtag';
function AnimatedNumber({
  value,
  prefix = '',
  suffix = '',
  duration = 1.5
}: {
  value: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, {
    once: true,
    amount: 0.1
  });
  const [display, setDisplay] = useState(0);
  const animatedRef = useRef(false);
  const animate = () => {
    if (animatedRef.current) return;
    animatedRef.current = true;
    const start = performance.now();
    const step = (now: number) => {
      const progress = Math.min((now - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(eased * value));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };
  useEffect(() => {
    if (isInView) {
      animate();
    }
  }, [isInView]);
  // Native IntersectionObserver fallback for mobile browsers
  useEffect(() => {
    if (!ref.current || animatedRef.current) return;
    if (typeof IntersectionObserver === 'undefined') {
      setDisplay(value);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        animate();
        observer.disconnect();
      }
    }, {
      threshold: 0.05
    });
    observer.observe(ref.current);
    // Final safety fallback — show value after 3s no matter what
    const safety = setTimeout(() => {
      if (!animatedRef.current) setDisplay(value);
    }, 3000);
    return () => {
      observer.disconnect();
      clearTimeout(safety);
    };
  }, [value]);
  return <span ref={ref} data-id="element-26">
      {prefix}
      {display.toLocaleString()}
      {suffix}
    </span>;
}
export function LandingPage() {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [formInteracted, setFormInteracted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [modalStep, setModalStep] = useState<'form' | 'booking'>('form');
  const [bookingCompleted, setBookingCompleted] = useState(false);
  const [calendarLoaded, setCalendarLoaded] = useState(false);
  const [calendarTimeout, setCalendarTimeout] = useState(false);
  useEffect(() => {
    document.title = 'School Launch';
    initPixel();
    initClarity();
    initGtag();
  }, []);
  // Listen for GHL booking confirmation via postMessage
  useEffect(() => {
    if (modalStep !== 'booking') return;
    const handleMessage = (event: MessageEvent) => {
      // Accept messages from GHL domains
      const trustedOrigins = ['https://api.leadconnectorhq.com', 'https://link.msgsndr.com'];
      if (trustedOrigins.some(origin => event.origin.startsWith(origin)) || event.origin.includes('leadconnectorhq') || event.origin.includes('msgsndr')) {
        // GHL sends various message formats on booking completion
        const data = typeof event.data === 'string' ? event.data : JSON.stringify(event.data);
        if (data.includes('booking') || data.includes('confirmed') || data.includes('scheduled') || data.includes('appointment') || data.includes('booked')) {
          setBookingCompleted(true);
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [modalStep]);
  // Preload GHL script as soon as modal opens (before booking step)
  useEffect(() => {
    if (showFormModal) {
      const existingScript = document.getElementById('lc-booking-script');
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://link.msgsndr.com/js/form_embed.js';
        script.type = 'text/javascript';
        script.id = 'lc-booking-script';
        document.body.appendChild(script);
      }
    }
  }, [showFormModal]);
  // Calendar load timeout
  useEffect(() => {
    if (modalStep !== 'booking') {
      setCalendarLoaded(false);
      setCalendarTimeout(false);
      return;
    }
    const timer = setTimeout(() => {
      if (!calendarLoaded) setCalendarTimeout(true);
    }, 12000);
    return () => clearTimeout(timer);
  }, [modalStep, calendarLoaded]);
  useEffect(() => {
    if (showFormModal) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setShowFormModal(false);
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = 'unset';
        window.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [showFormModal]);
  const handleUnmute = () => {
    if (videoRef.current) {
      videoRef.current.muted = false;
      videoRef.current.currentTime = 0;
      setIsMuted(false);
      trackVideoPlay();
    }
  };
  const handleCtaClick = (e: React.MouseEvent, ctaName: string) => {
    e.preventDefault();
    trackCtaClick(ctaName);
    setModalStep('form');
    setBookingCompleted(false);
    setCalendarLoaded(false);
    setCalendarTimeout(false);
    setShowFormModal(true);
  };
  const handleFormFocus = () => {
    if (!formInteracted) {
      setFormInteracted(true);
      trackFormStart();
    }
  };
  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(false);
    const form = e.currentTarget;
    const formData = new FormData(form);
    const urlParams = new URLSearchParams();
    urlParams.append('full_name', formData.get('full_name') as string);
    urlParams.append('school_name', formData.get('school_name') as string);
    urlParams.append('email', formData.get('email') as string);
    urlParams.append('phone', formData.get('phone') as string);
    urlParams.append('enrollment_capacity', formData.get('enrollment_capacity') as string);
    try {
      await fetch('https://services.leadconnectorhq.com/hooks/OtyTgdaoNhRuZomfMmhH/webhook-trigger/9943c17c-6c4f-4bcc-93c3-ccebd1663f11', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: urlParams.toString(),
        mode: 'no-cors'
      });
      trackLead();
      setModalStep('booking');
    } catch (err) {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="min-h-screen bg-black text-white font-sans selection:bg-amber-500 selection:text-black overflow-x-hidden" data-id="element-27">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur-md border-b border-zinc-800" data-id="element-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-center md:justify-between" data-id="element-29">
          <a href="#" className="flex items-center" aria-label="SchoolLaunch - Home" data-id="element-30">
            <img src="https://cdn.magicpatterns.com/uploads/3aM3DA6U6f25P9EHkUZYxu/SCHOOL_LAUNCH_LOGO_black_2.png" alt="SchoolLaunch" className="h-10 w-auto" data-id="element-31" />
          </a>
          <Button variant="primary" size="sm" className="hidden md:flex" onClick={e => handleCtaClick(e, 'nav_apply')} data-id="element-32">
            Apply Now to See If You Qualify
          </Button>
        </div>
      </nav>

      {/* 1. Hero Section */}
      <section className="md:pt-32 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-[100px] pb-[20px]" data-id="element-33">
        <div className="text-center max-w-4xl mx-auto mb-8 md:mb-12" data-id="element-34">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-amber-500/10 text-amber-500 font-medium text-xs md:text-sm mb-4 md:mb-8 border border-amber-500/20" data-id="element-35">
            Exclusive for Learning Centers Looking to Grow
          </div>

          <h1 className="text-3xl sm:text-5xl md:text-7xl font-extrabold tracking-tight mb-3 md:mb-6 leading-tight" data-id="element-36">
            Is Your School
            <br className="sm:hidden" data-id="element-37" /> Losing Students to{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600 whitespace-nowrap" data-id="element-38">
              Empty Seats?
            </span>
          </h1>

          <p className="text-base md:text-2xl text-zinc-400 mb-6 md:mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-39">
            We help learning centers add 40+ students in 90 days using our
            proven "School Launch" marketing system.
          </p>

          {/* VSL Video */}
          <div className="relative w-full max-w-4xl mx-auto aspect-video bg-zinc-900 rounded-2xl border border-zinc-800 shadow-2xl overflow-hidden mb-6 md:mb-12 group" data-id="element-40">
            <video ref={videoRef} className="w-full h-full object-cover" autoPlay muted={isMuted} loop playsInline controls={!isMuted} preload="metadata" data-id="element-41">
              <source src="https://cdn.magicpatterns.com/uploads/29qEefnVojXxrob6CqnZT6/School Launch VSL.mp4" type="video/mp4" data-id="element-42" />
              Your browser does not support the video tag.
            </video>

            {isMuted && <div className="absolute inset-0 cursor-pointer flex flex-col justify-end p-4 md:p-6 bg-gradient-to-t from-black/80 via-transparent to-transparent" onClick={handleUnmute} data-id="element-43">
                <div className="flex items-center gap-2 md:gap-3 bg-black/60 backdrop-blur-md border border-white/10 text-white px-3 py-2 md:px-4 md:py-2.5 rounded-full w-fit hover:bg-black/80 transition-colors" data-id="element-44">
                  <div className="relative flex h-3 w-3 md:h-4 md:w-4 items-center justify-center" data-id="element-45">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" data-id="element-46"></span>
                    <VolumeX className="h-3 w-3 md:h-4 md:w-4 relative z-10 text-amber-500" data-id="element-47" />
                  </div>
                  <span className="font-medium text-xs md:text-sm" data-id="element-48">
                    Click to Unmute
                  </span>
                </div>
              </div>}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6 mb-8 md:mb-12" data-id="element-49">
            <Button size="lg" className="w-full sm:w-auto text-base md:text-lg group" onClick={e => handleCtaClick(e, 'hero_apply')} data-id="element-50">
              Apply Now to See if You Qualify
              <ArrowRightIcon className="ml-2 h-4 w-4 md:h-5 md:w-5 group-hover:translate-x-1 transition-transform" data-id="element-51" />
            </Button>
          </div>
        </div>
      </section>

      {/* 3. Solution Section */}
      <section className="py-16 md:py-24 pt-[30px]" data-id="element-52">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-53">
          <div className="text-center mb-10 md:mb-16" data-id="element-54">
            <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-55">
              The School Launch Enrollment System
            </h2>
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto" data-id="element-56">
              We don't just run ads. We build a predictable machine that turns
              local parents into enrolled families.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-57">
            <div className="relative" data-id="element-58">
              <div className="absolute top-0 left-6 -ml-px h-full w-0.5 bg-zinc-800 hidden md:block" data-id="element-59"></div>
              <div className="rounded-xl border p-6 bg-zinc-900 border-zinc-800 text-white shadow-md shadow-black/20 relative z-10 h-full backdrop-blur" data-id="element-60">
                <div className="w-12 h-12 bg-amber-500 text-black rounded-full flex items-center justify-center font-bold text-xl mb-6" data-id="element-61">
                  1
                </div>

                <h3 className="text-2xl font-bold mb-3" data-id="element-62">
                  Hyper-Local Targeting
                </h3>
                <p className="text-zinc-400" data-id="element-63">
                  We pinpoint parents within a 5-10 mile radius of your center
                  who are actively looking for premium childcare solutions right
                  now.
                </p>
              </div>
            </div>

            <div className="relative" data-id="element-64">
              <div className="absolute top-0 left-6 -ml-px h-full w-0.5 bg-zinc-800 hidden md:block" data-id="element-65"></div>
              <div className="rounded-xl border p-6 bg-zinc-900 border-zinc-800 text-white shadow-md shadow-black/20 relative z-10 h-full backdrop-blur" data-id="element-66">
                <div className="w-12 h-12 bg-amber-500 text-black rounded-full flex items-center justify-center font-bold text-xl mb-6" data-id="element-67">
                  2
                </div>

                <h3 className="text-2xl font-bold mb-3" data-id="element-68">Automated Nurturing</h3>
                <p className="text-zinc-400" data-id="element-69">
                  Our system automatically follows up with leads via SMS and
                  email within 5 minutes, booking them directly into your tour
                  calendar.
                </p>
              </div>
            </div>

            <div className="relative" data-id="element-70">
              <div className="rounded-xl border p-6 bg-zinc-900 border-zinc-800 text-white shadow-md shadow-black/20 relative z-10 h-full backdrop-blur" data-id="element-71">
                <div className="w-12 h-12 bg-amber-500 text-black rounded-full flex items-center justify-center font-bold text-xl mb-6" data-id="element-72">
                  3
                </div>

                <h3 className="text-2xl font-bold mb-3" data-id="element-73">Predictable Tours</h3>
                <p className="text-zinc-400" data-id="element-74">
                  You wake up to a calendar full of qualified tour appointments.
                  All you have to do is show them your amazing facility and
                  enroll them.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12" data-id="element-75">
            <a href="#" onClick={e => handleCtaClick(e, 'solution_apply')} className="inline-flex items-center justify-center font-bold rounded-md text-base sm:text-lg px-8 py-4 bg-amber-500 text-black hover:bg-amber-400 shadow-lg shadow-amber-500/20 hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-black" data-id="element-76">
              Apply Now to See If You Qualify
              <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-77" />
            </a>
          </div>
        </div>
      </section>

      {/* 4. Proof Section */}
      <section className="py-16 md:py-24 bg-zinc-950 border-y border-zinc-900 pt-[30px]" data-id="element-78">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-79">
          <div className="max-w-2xl mx-auto text-center" data-id="element-80">
            <div data-id="element-81">
              <h2 className="text-3xl md:text-5xl font-bold mb-6 text-center" data-id="element-82">
                Don't just take our word for it.
              </h2>
              <p className="text-xl text-zinc-400 mb-10" data-id="element-83">
                We've helped dozens of centers reach full capacity and start
                waitlists.
              </p>
              <div className="space-y-6" data-id="element-84">
                <motion.div className="flex items-center gap-4" initial={{
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-50px'
              }} transition={{
                duration: 0.4
              }} data-id="element-85">
                  <div className="text-4xl font-bold text-amber-500 tabular-nums" data-id="element-86">
                    <AnimatedNumber value={42} duration={1.2} data-id="element-87" />
                  </div>
                  <div className="text-lg text-zinc-300" data-id="element-88">
                    Average new enrollments in first 90 days
                  </div>
                </motion.div>
                <div className="w-full h-px bg-zinc-800" data-id="element-89"></div>
                <motion.div className="flex items-center gap-4" initial={{
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-50px'
              }} transition={{
                duration: 0.4,
                delay: 0.15
              }} data-id="element-90">
                  <div className="text-4xl font-bold text-amber-500 tabular-nums" data-id="element-91">
                    <AnimatedNumber value={14} prefix="$" suffix="k" duration={1.4} data-id="element-92" />
                  </div>
                  <div className="text-lg text-zinc-300" data-id="element-93">
                    Average monthly recurring revenue added
                  </div>
                </motion.div>
                <div className="w-full h-px bg-zinc-800" data-id="element-94"></div>
                <motion.div className="flex items-center gap-4" initial={{
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-50px'
              }} transition={{
                duration: 0.4,
                delay: 0.3
              }} data-id="element-95">
                  <div className="text-4xl font-bold text-amber-500 tabular-nums" data-id="element-96">
                    <AnimatedNumber value={50} suffix="%" duration={1.6} data-id="element-97" />
                  </div>
                  <div className="text-lg text-zinc-300" data-id="element-98">
                    Tour-to-enrollment conversion rate
                  </div>
                </motion.div>
              </div>

              <div className="mt-10 text-center" data-id="element-99">
                <a href="#" onClick={e => handleCtaClick(e, 'proof_apply')} className="inline-flex items-center justify-center font-bold rounded-md text-base sm:text-lg px-8 py-4 bg-amber-500 text-black hover:bg-amber-400 shadow-lg shadow-amber-500/20 hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-zinc-950" data-id="element-100">
                  Apply Now to See If You Qualify
                  <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-101" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Calendar Booking Placeholder */}
      <section className="py-16 md:py-24 bg-zinc-950 border-y border-zinc-900 hidden" data-id="element-102">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-103">
          <h2 className="text-3xl font-bold mb-4" data-id="element-104">
            Step 2: Pick a time for your call
          </h2>
          <p className="text-zinc-400 mb-8" data-id="element-105">
            Select a time below that works best for you.
          </p>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 min-h-[600px] flex items-center justify-center" data-id="element-106">
            <div className="flex flex-col items-center text-zinc-500" data-id="element-107">
              <CalendarIcon className="h-12 w-12 mb-4 opacity-50" data-id="element-108" />
              <p data-id="element-109">Calendar Widget Embed Goes Here</p>
            </div>
          </div>
        </div>
      </section>

      {/* 8. Final CTA */}
      <section className="py-16 md:py-32 bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 text-center relative overflow-hidden" role="region" aria-label="Call to action" data-id="element-110">
        <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle_at_center,black_1.5px,transparent_1.5px)] bg-[size:16px_16px]" aria-hidden="true" data-id="element-111"></div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-112">
          <h2 className="text-4xl md:text-6xl font-extrabold mb-6 text-black leading-tight" data-id="element-113">
            Stop losing money on empty seats.
          </h2>
          <p className="text-lg md:text-2xl font-medium mb-10 text-amber-950 max-w-2xl mx-auto leading-relaxed" data-id="element-114">
            Apply now and let us show you exactly how we'll pack your center
            with new enrollments.
          </p>
          <a href="#" onClick={e => handleCtaClick(e, 'final_cta_apply')} className="inline-flex items-center justify-center font-bold rounded-md text-sm px-6 py-3 sm:text-lg sm:px-10 sm:py-5 bg-black text-white hover:bg-zinc-900 shadow-2xl shadow-black/25 hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 focus:ring-offset-amber-500" data-id="element-115">
            Apply Now to See If You Qualify
            <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-116" />
          </a>
          <p className="mt-6 text-sm font-semibold text-amber-900 tracking-wide" data-id="element-117">
            No obligation. Just pure value.
          </p>
        </div>
      </section>

      {/* 7. Checklist Section */}
      <section className="py-16 md:py-24 pt-[30px] pb-[30px]" data-id="element-118">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-119">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-120">
            <div data-id="element-121">
              <h2 className="text-3xl md:text-4xl font-bold mb-8" data-id="element-122">
                Who this is for:
              </h2>
              <ul className="space-y-6" data-id="element-123">
                {[{
                title: 'Established Centers',
                desc: 'You have a great facility and curriculum, you just need more parents to see it.'
              }, {
                title: 'Capacity to Grow',
                desc: 'You have at least 15-20 empty spots you want to fill in the next 90 days.'
              }, {
                title: 'Ready to Invest',
                desc: 'You understand that marketing is an investment that yields a return, not an expense.'
              }].map((item, i) => <motion.li key={item.title} className="flex items-start gap-4" initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true,
                margin: '-50px'
              }} transition={{
                duration: 0.4,
                delay: i * 0.15,
                ease: 'easeOut'
              }} data-id="element-124">
                    <motion.div className="shrink-0 mt-1" initial={{
                  scale: 0,
                  rotate: -90
                }} whileInView={{
                  scale: 1,
                  rotate: 0
                }} viewport={{
                  once: true,
                  margin: '-50px'
                }} transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 15,
                  delay: i * 0.15 + 0.2
                }} data-id="element-125">
                      <CheckCircle2Icon className="h-6 w-6 text-amber-500" data-id="element-126" />
                    </motion.div>
                    <div data-id="element-127">
                      <h4 className="text-xl font-bold mb-1" data-id="element-128">{item.title}</h4>
                      <p className="text-zinc-400" data-id="element-129">{item.desc}</p>
                    </div>
                  </motion.li>)}
              </ul>
            </div>

            <div data-id="element-130">
              <h2 className="text-3xl md:text-4xl font-bold mb-8" data-id="element-131">
                Who this is NOT for:
              </h2>
              <ul className="space-y-6" data-id="element-132">
                {[{
                title: 'Brand New Startups',
                desc: "If you haven't opened your doors yet or lack basic licensing, we can't help you yet."
              }, {
                title: 'Poor Quality Centers',
                desc: "If your main issue is high turnover because of poor service, marketing won't fix that."
              }, {
                title: 'Passive Owners',
                desc: "If you're just looking for free advice and aren't ready to take action to grow your center."
              }].map((item, i) => <motion.li key={item.title} className="flex items-start gap-4" initial={{
                opacity: 0,
                x: 20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true,
                margin: '-50px'
              }} transition={{
                duration: 0.4,
                delay: i * 0.15,
                ease: 'easeOut'
              }} data-id="element-133">
                    <motion.div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center shrink-0 mt-1" initial={{
                  scale: 0
                }} whileInView={{
                  scale: 1
                }} viewport={{
                  once: true,
                  margin: '-50px'
                }} transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 15,
                  delay: i * 0.15 + 0.2
                }} data-id="element-134">
                      <motion.div className="w-2 h-2 rounded-full bg-red-500" animate={{
                    scale: [1, 1.5, 1],
                    opacity: [1, 0.7, 1]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: i * 0.3
                  }} data-id="element-135" />
                    </motion.div>
                    <div data-id="element-136">
                      <h4 className="text-xl font-bold mb-1" data-id="element-137">{item.title}</h4>
                      <p className="text-zinc-400" data-id="element-138">{item.desc}</p>
                    </div>
                  </motion.li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Form Modal */}
      <AnimatePresence data-id="element-139">
        {showFormModal && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-140">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowFormModal(false)} data-id="element-141" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className={`relative w-full rounded-2xl border p-6 bg-zinc-900 border-zinc-800 text-white shadow-2xl shadow-black/40 overflow-hidden ${modalStep === 'booking' ? 'max-w-3xl max-h-[90vh] overflow-y-auto' : 'max-w-2xl'}`} data-id="element-142">
              <div className="absolute top-0 left-0 right-0 h-1 bg-amber-500" aria-hidden="true" data-id="element-143"></div>

              <button onClick={() => setShowFormModal(false)} className="absolute top-4 right-4 p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-full transition-colors" aria-label="Close modal" data-id="element-144">
                <XIcon className="h-5 w-5" data-id="element-145" />
              </button>

              {modalStep === 'form' ? <>
                  <div className="text-center mb-8 mt-4" data-id="element-146">
                    <h2 className="text-2xl md:text-3xl font-bold mb-3" data-id="element-147">
                      Ready to fill your empty seats?
                    </h2>
                    <p className="text-zinc-400" data-id="element-148">
                      Apply below to see if your center qualifies for our growth
                      program.
                    </p>
                  </div>

                  <form className="space-y-4" onSubmit={handleFormSubmit} onFocus={handleFormFocus} aria-label="Strategy call application form" data-id="element-149">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-150">
                      <Input label="Full Name" name="full_name" placeholder="Jane Doe" required data-id="element-151" />
                      <Input label="School Name" name="school_name" placeholder="Sunshine Preschool" required data-id="element-152" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-153">
                      <Input label="Work Email" name="email" type="email" placeholder="jane@school.com" required data-id="element-154" />
                      <Input label="Phone Number" name="phone" type="tel" placeholder="(555) 123-4567" required data-id="element-155" />
                    </div>
                    <Select label="Current Enrollment Capacity" name="enrollment_capacity" options={[{
                value: 'under-50',
                label: 'Under 50% Full'
              }, {
                value: '50-75',
                label: '50% - 75% Full'
              }, {
                value: '75-90',
                label: '75% - 90% Full'
              }, {
                value: 'over-90',
                label: 'Over 90% Full (Looking to expand)'
              }]} required data-id="element-156" />
                    {submitError && <p className="text-red-400 text-sm text-center" data-id="element-157">
                        Something went wrong. Please try again.
                      </p>}
                    <div className="pt-4" data-id="element-158">
                      <Button size="lg" fullWidth className="text-lg" disabled={isSubmitting} data-id="element-159">
                        {isSubmitting ? 'Sending...' : 'See If You Qualify'}
                      </Button>
                      <p className="text-center text-sm text-zinc-500 mt-4" data-id="element-160">
                        100% secure. We never share your information.
                      </p>
                    </div>
                  </form>
                </> : <div className="mt-4" data-id="element-161">
                  <div className="text-center mb-6" data-id="element-162">
                    <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-500/10 border border-amber-500/40 flex items-center justify-center" data-id="element-163">
                      <CheckCircle2Icon className="h-8 w-8 text-amber-500" data-id="element-164" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold mb-2" data-id="element-165">
                      Congrats! You qualify.
                    </h2>
                    <p className="text-zinc-400" data-id="element-166">
                      Schedule a meeting with us to get started.
                    </p>
                  </div>

                  <div className="rounded-xl overflow-hidden border border-zinc-800 bg-white mb-6 relative" style={{
              minHeight: '600px'
            }} data-id="element-167">
                    {!calendarLoaded && <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-10" data-id="element-168">
                        <div className="w-10 h-10 border-4 border-zinc-200 border-t-amber-500 rounded-full animate-spin mb-4" data-id="element-169"></div>
                        <p className="text-zinc-500 text-sm font-medium" data-id="element-170">
                          Loading calendar...
                        </p>
                        {calendarTimeout && <div className="mt-4 text-center" data-id="element-171">
                            <p className="text-zinc-400 text-xs mb-2" data-id="element-172">
                              Taking longer than expected.
                            </p>
                            <button onClick={() => {
                    setCalendarLoaded(false);
                    setCalendarTimeout(false);
                    setModalStep('form');
                    setTimeout(() => setModalStep('booking'), 100);
                  }} className="text-amber-600 text-sm font-semibold hover:text-amber-500 underline underline-offset-2 transition-colors" data-id="element-173">
                              Try again
                            </button>
                          </div>}
                      </div>}
                    <iframe src="https://api.leadconnectorhq.com/widget/booking/hLjcYjbdfnzOTuq8XU8Y" style={{
                width: '100%',
                border: 'none',
                overflow: 'hidden',
                minHeight: '600px'
              }} scrolling="no" id="hLjcYjbdfnzOTuq8XU8Y_1776881478506" title="Schedule a meeting" onLoad={() => setCalendarLoaded(true)} data-id="element-174" />
                  </div>

                  <Button size="lg" fullWidth className={`text-lg ${!bookingCompleted ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={!bookingCompleted} onClick={() => {
              setShowFormModal(false);
              navigate('/thank-you');
            }} data-id="element-175">
                    {bookingCompleted ? 'Continue' : 'Book Your Call to Continue'}
                    <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-176" />
                  </Button>
                </div>}
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* 9. Footer */}
      <footer className="bg-black py-12 border-t border-zinc-900" data-id="element-177">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6" data-id="element-178">
          <a href="#" className="flex items-center" aria-label="SchoolLaunch - Home" data-id="element-179">
            <img src="https://cdn.magicpatterns.com/uploads/3aM3DA6U6f25P9EHkUZYxu/SCHOOL_LAUNCH_LOGO_black_2.png" alt="SchoolLaunch" className="h-8 w-auto" data-id="element-180" />
          </a>
          <div className="text-zinc-500 text-sm text-center" data-id="element-181">
            &copy; {new Date().getFullYear()} Engage Media Group. All rights
            reserved.
          </div>
          <div className="flex gap-6 text-sm text-zinc-400" data-id="element-182">
            <a href="/privacy-policy" className="hover:text-amber-500 transition-colors" data-id="element-183">
              Privacy Policy
            </a>
            <a href="/terms-and-conditions" className="hover:text-amber-500 transition-colors" data-id="element-184">
              Terms
            </a>
          </div>
        </div>
      </footer>
    </div>;
}